import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(typeof iso === "object" && iso.date ? iso.date.replace(" ", "T") : iso);
  return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function ScheduleModal({ schedule, cohorts, onClose, onSaved }) {
  const isEdit = !!schedule?.id;
  const [form, setForm] = useState(isEdit
    ? { topic: schedule.topic || "", link: schedule.link || "", recording_link: schedule.recording_link || "", start_time: schedule.start_time?.date?.slice(0, 16) || "", type: schedule.type || "lecture" }
    : { topic: "", link: "", recording_link: "", start_time: "", cohort_id: "", course_id: "", module_id: "", type: "lecture" }
  );
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
        fd.append("_method", "PATCH");
        await client.post(`/schedules/${schedule.id}`, fd);
        toast.success("Schedule updated.");
      } else {
        await client.post("/schedules", form);
        toast.success("Schedule created.");
      }
      onSaved(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{isEdit ? "Edit" : "New"} Session</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Topic *</label>
            <input required value={form.topic} onChange={set("topic")} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Start Time *</label>
            <input required type="datetime-local" value={form.start_time} onChange={set("start_time")} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Meeting Link</label>
            <input type="url" value={form.link} onChange={set("link")} placeholder="https://meet.google.com/…" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Recording Link</label>
            <input type="url" value={form.recording_link} onChange={set("recording_link")} placeholder="https://vimeo.com/…" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Type</label>
            <select value={form.type} onChange={set("type")} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30">
              <option value="lecture">Lecture</option>
              <option value="workshop">Workshop</option>
              <option value="qa_session">Q&A Session</option>
              <option value="makeup">Makeup Class</option>
              <option value="other">Other</option>
            </select>
            {form.type === "makeup" && (
              <p className="text-xs text-gray-400 mt-1">Shows on the student dashboard like a normal class, but attendance isn't tracked.</p>
            )}
          </div>
          {!isEdit && (
            <>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cohort ID</label>
                <input type="number" value={form.cohort_id} onChange={set("cohort_id")} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Course ID</label>
                <input type="number" value={form.course_id} onChange={set("course_id")} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Module ID</label>
                <input type="number" value={form.module_id} onChange={set("module_id")} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
              </div>
            </>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-3 rounded-xl bg-[#294a93] hover:bg-[#1e3a7a] text-white text-sm font-bold transition-colors disabled:opacity-60">
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Session"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminSchedulesPage() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    // Use the existing v2 schedules endpoint
    client.get("/v2/schedules").then(res => {
      const list = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setSchedules(list);
    }).catch(() => toast.error("Failed to load schedules.")).finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      {modal && <ScheduleModal schedule={modal.schedule} onClose={() => setModal(null)} onSaved={load} />}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Schedules</h1>
          <p className="text-sm text-gray-500 mt-0.5">All class sessions</p>
        </div>
        <button onClick={() => setModal({})} className="bg-[#294a93] hover:bg-[#1e3a7a] text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors">
          + New Session
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : schedules.length === 0 ? (
          <div className="p-16 text-center"><p className="text-4xl mb-3">📅</p><p className="text-sm font-medium text-gray-600">No schedules found</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{["Topic", "Course", "Start Time", "Status", "Link", "Actions"].map(h => (
                <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {schedules.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800 max-w-[180px] truncate">
                    {s.topic || "—"}
                    {s.type === "makeup" && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-violet-100 text-violet-700 align-middle">Makeup</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{s.course?.title || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmt(s.start_time)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                      s.status === "live" ? "bg-red-100 text-red-700" :
                      s.status === "recorded" ? "bg-green-100 text-green-700" :
                      "bg-blue-100 text-blue-700"
                    }`}>{s.status || "upcoming"}</span>
                  </td>
                  <td className="px-4 py-3">
                    {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="text-xs text-[#294a93] font-semibold hover:underline">Join →</a>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setModal({ schedule: s })} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-3 py-1.5 rounded-lg transition-colors">Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
